// People on a tenant, who approves, and what each person is told.
import { z } from "zod";
import { IsoDateTime, Uuid, withoutLink } from "./common";
import { ROLES, UiLang } from "./identity";
import { ApproverContact, NOTIFY_CHANNELS, NotifyChannelName } from "./studio";

const MemberRole = z.enum(ROLES);

/** Where a tenant's people come from: `direct` means they are invited here, `host` means a host's SSO owns them. */
export const PEOPLE_SOURCES = ["direct", "host"] as const;
export const PeopleSource = z.enum(PEOPLE_SOURCES);
export type PeopleSource = z.infer<typeof PeopleSource>;

/** On a host-fed tenant: this long with no sign-in and a person stops approving, until they sign in again. A host may never say they left. */
export const MEMBER_LAPSE_DAYS = 90;

export const TeamMemberDto = z.object({
  id: Uuid,
  userId: Uuid,
  name: z.string(),
  /** Null for a viewer looking at somebody else. */
  email: z.string().nullable(),
  role: MemberRole,
  isYou: z.boolean(),
  joinedAt: IsoDateTime,
  /** Only an owner of a host-fed tenant is told, and only about someone who lapsed: when they last signed in, or joined if they never did. */
  lapsedLastSignIn: IsoDateTime.nullable(),
});
export type TeamMemberDto = z.infer<typeof TeamMemberDto>;

export const TeamMemberPage = z.object({
  items: z.array(TeamMemberDto),
  nextCursor: z.string().nullable(),
  source: PeopleSource,
  /** People whose role comes from the agency above this tenant. They are managed there, never here. */
  fromAgency: z.array(z.object({ userId: Uuid, name: z.string(), email: z.string().nullable(), role: MemberRole, lapsedLastSignIn: IsoDateTime.nullable() })),
});
export type TeamMemberPage = z.infer<typeof TeamMemberPage>;

/** The answer when a host says a person left. `stillThroughAgency`: a role over the tenant's agency remains, and only a sign-in naming that agency changes it. */
export const MemberLeftDto = z.object({
  removed: z.boolean(),
  stillThroughAgency: z.boolean(),
  wasApprover: z.boolean(),
  leadOwnersStopped: z.number().int().nonnegative(),
});
export type MemberLeftDto = z.infer<typeof MemberLeftDto>;

export const TeamMemberPatch = z.strictObject({ role: MemberRole });
export type TeamMemberPatch = z.infer<typeof TeamMemberPatch>;

export const INVITATION_DAYS = 7;
export const INVITATION_STATUSES = ["pending", "accepted", "revoked", "expired"] as const;
export const InvitationStatus = z.enum(INVITATION_STATUSES);
export type InvitationStatus = z.infer<typeof InvitationStatus>;
export const INVITATION_DELIVERIES = ["sent", "failed", "unavailable"] as const;
export const InvitationDelivery = z.enum(INVITATION_DELIVERIES);
export type InvitationDelivery = z.infer<typeof InvitationDelivery>;

export const InvitationDto = z.object({
  id: Uuid,
  email: z.string(),
  role: MemberRole,
  status: InvitationStatus,
  invitedByName: z.string(),
  expiresAt: IsoDateTime,
  createdAt: IsoDateTime,
  lastDelivery: InvitationDelivery.nullable(),
});
export type InvitationDto = z.infer<typeof InvitationDto>;

export const InvitationPage = z.object({ items: z.array(InvitationDto), nextCursor: z.string().nullable() });
export type InvitationPage = z.infer<typeof InvitationPage>;

export const InvitationRequest = z.strictObject({ email: z.email().max(320), role: MemberRole });
export type InvitationRequest = z.infer<typeof InvitationRequest>;

/** Reading an invitation spends nothing. It is a POST so the token stays out of URLs and logs. */
export const InvitationPreviewRequest = z.strictObject({ token: z.string().min(16).max(200) });
export const InvitationPreview = z.object({
  tenantName: z.string(),
  role: MemberRole,
  invitedByName: z.string(),
  email: z.string(),
  /** A person this address already belongs to joins with one tap; anyone else gives a name first. */
  needsName: z.boolean(),
  expiresAt: IsoDateTime,
});
export type InvitationPreview = z.infer<typeof InvitationPreview>;

export const InvitationAcceptRequest = z.strictObject({ token: z.string().min(16).max(200), name: withoutLink(z.string().trim().min(2).max(120)).optional(), lang: UiLang.optional() });
export type InvitationAcceptRequest = z.infer<typeof InvitationAcceptRequest>;

// ---------------------------------------------------------------------------
// Approvers
// ---------------------------------------------------------------------------

export const ApproverChoice = z.union([
  z.strictObject({ userId: Uuid }),
  z
    .strictObject({ name: withoutLink(z.string().trim().min(2).max(120)), email: z.email().max(320).nullable().optional(), telegram: z.string().trim().min(1).max(100).nullable().optional() })
    .refine((value) => Boolean(value.email || value.telegram), { message: "approver_needs_address" }),
]);
export type ApproverChoice = z.infer<typeof ApproverChoice>;

export const SetApproversRequest = z.strictObject({ approvers: z.array(ApproverChoice).max(30) });
export type SetApproversRequest = z.infer<typeof SetApproversRequest>;

export const ApproversDto = z.object({
  /** Who is chosen. Empty means whoever can approve here, listed in `candidates`, by e-mail. */
  chosen: z.array(ApproverContact),
  candidates: z.array(z.object({ userId: Uuid, name: z.string(), email: z.string().nullable(), role: MemberRole })),
  /** Those of `chosen` who lapsed: on the owner's list, but asked about nothing until they sign in. Told to an owner only. */
  lapsed: z.array(Uuid),
});
export type ApproversDto = z.infer<typeof ApproversDto>;

// ---------------------------------------------------------------------------
// What each person is told
// ---------------------------------------------------------------------------

export const NOTIFY_EVENTS = ["approval_needed", "lead_new", "lead_assigned", "lead_reminder", "lead_digest", "channel_trouble", "post_paused", "ads_stopped", "ai_cap_80", "weekly_report", "data_stale", "support_reply"] as const;
export const NotifyEvent = z.enum(NOTIFY_EVENTS);
export type NotifyEvent = z.infer<typeof NotifyEvent>;

export const NOTIFY_UNAVAILABLE_REASONS = ["no_email", "no_bot", "not_linked", "tenant_has_no_sms", "no_phone", "no_push_keys", "no_device"] as const;
export const NotifyUnavailableReason = z.enum(NOTIFY_UNAVAILABLE_REASONS);
export type NotifyUnavailableReason = z.infer<typeof NotifyUnavailableReason>;

/** The four channels of the preferences screen. WhatsApp stays a contact detail of approvers: nobody chooses it here. */
export const PREFERENCE_CHANNELS = ["email", "telegram", "sms", "push"] as const satisfies ReadonlyArray<(typeof NOTIFY_CHANNELS)[number]>;
export const PreferenceChannel = z.enum(PREFERENCE_CHANNELS);
export type PreferenceChannel = z.infer<typeof PreferenceChannel>;

/** A browser that asked to be told. Enough for a person to recognise it and take it off; the endpoint and keys stay on the server. */
export const PushDeviceDto = z.object({ id: z.string(), userAgent: z.string().nullable(), createdAt: IsoDateTime, lastSuccessAt: IsoDateTime.nullable() });
export type PushDeviceDto = z.infer<typeof PushDeviceDto>;

export const NotifyPreferencesDto = z.object({
  timezone: z.string(),
  channels: z.array(z.object({ channel: PreferenceChannel, available: z.boolean(), reason: NotifyUnavailableReason.nullable() })),
  events: z.array(
    z.object({
      event: NotifyEvent,
      channels: z.array(NotifyChannelName),
      /** The person chose this; otherwise it shows what happens today without a choice. */
      custom: z.boolean(),
      /** Only people on the approvers list are asked to approve. */
      applies: z.boolean(),
      urgent: z.boolean(),
    }),
  ),
  quietHours: z.object({ start: z.number().int(), end: z.number().int() }).nullable(),
  telegram: z.object({ linked: z.boolean(), linkedAt: IsoDateTime.nullable() }),
  phone: z.string().nullable(),
  /** `publicKey` is null when this deployment has no VAPID keys. */
  push: z.object({ publicKey: z.string().nullable(), devices: z.array(PushDeviceDto) }),
  heldCount: z.number().int(),
  nextRelease: IsoDateTime.nullable(),
});
export type NotifyPreferencesDto = z.infer<typeof NotifyPreferencesDto>;

export const NotifyCellPatch = z.strictObject({ event: NotifyEvent, channel: PreferenceChannel, enabled: z.boolean() });
export type NotifyCellPatch = z.infer<typeof NotifyCellPatch>;

export const QuietHoursRequest = z.strictObject({
  quietHours: z
    .strictObject({ start: z.number().int().min(0).max(23), end: z.number().int().min(0).max(23) })
    .refine((value) => value.start !== value.end, { message: "quiet_hours_empty" })
    .nullable(),
});
export type QuietHoursRequest = z.infer<typeof QuietHoursRequest>;

export const NotifyPhoneRequest = z.strictObject({ phone: z.string().trim().min(5).max(30).nullable() });
export type NotifyPhoneRequest = z.infer<typeof NotifyPhoneRequest>;

/** What `PushSubscription.toJSON()` gives in a browser, and the language this device reads. */
export const PushSubscribeRequest = z.strictObject({
  endpoint: z.string().trim().min(10).max(2048),
  keys: z.object({ p256dh: z.string().trim().min(80).max(120), auth: z.string().trim().min(16).max(32) }),
  lang: UiLang,
});
export type PushSubscribeRequest = z.infer<typeof PushSubscribeRequest>;

/** `deviceToken` is shown once and stays on the device; it opens a session here when a notice is tapped. */
export const PushSubscribedDto = z.object({ deviceId: z.string(), deviceToken: z.string(), preferences: NotifyPreferencesDto });
export type PushSubscribedDto = z.infer<typeof PushSubscribedDto>;

/** A way to arrive signed in where an iframe's memory or a browser's storage does not reach: another tab, or an app on the home screen. */
export const PushHandoffDto = z.object({ code: z.string(), expiresAt: IsoDateTime });
export type PushHandoffDto = z.infer<typeof PushHandoffDto>;

export const PushHandoffRequest = z.strictObject({ code: z.string().trim().min(4).max(32) });
export type PushHandoffRequest = z.infer<typeof PushHandoffRequest>;

export const PushDeviceSessionRequest = z.strictObject({ deviceToken: z.string().trim().min(20).max(200) });
export type PushDeviceSessionRequest = z.infer<typeof PushDeviceSessionRequest>;

export const TelegramLinkDto = z.object({ url: z.string(), expiresAt: IsoDateTime });
export type TelegramLinkDto = z.infer<typeof TelegramLinkDto>;

/** What Telegram posts: only the fields a link needs. Anything else in an update is not this route's business. */
export const TelegramUpdate = z.object({
  message: z
    .object({
      text: z.string().optional(),
      chat: z.object({ id: z.union([z.number(), z.string()]), type: z.string().optional() }),
    })
    .optional(),
});
export type TelegramUpdate = z.infer<typeof TelegramUpdate>;
